/**
 * Noir Plates — shared lightweight motion helpers.
 * No external dependencies. Everything here is opt-in via data attributes,
 * so sections that don't use it pay zero cost. Respects prefers-reduced-motion.
 */
(function () {
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ */
  /* Subtle scroll parallax — transform-only, rAF-throttled.             */
  /* Usage: data-noir-parallax [data-noir-parallax-strength="14"]        */
  /* ------------------------------------------------------------------ */
  function initParallax() {
    if (reduceMotion) return;
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-noir-parallax]'));
    if (!els.length) return;

    var ticking = false;

    function update() {
      var viewportHeight = window.innerHeight;
      els.forEach(function (el) {
        var strength = parseFloat(el.getAttribute('data-noir-parallax-strength')) || 12;
        var rect = el.getBoundingClientRect();
        var centerOffset = rect.top + rect.height / 2 - viewportHeight / 2;
        var ratio = centerOffset / viewportHeight;
        var translate = Math.max(Math.min(ratio * strength, strength), -strength);
        el.style.setProperty('--noir-parallax-y', translate.toFixed(2) + 'px');
      });
      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  /* ------------------------------------------------------------------ */
  /* Sticky scroll-story stepper — highlights the active text step as    */
  /* it crosses a horizontal band in the viewport, while the media stays */
  /* sticky (pure CSS position:sticky; this only toggles active state).  */
  /* Usage: [data-noir-story] wrapping [data-noir-story-step] items.     */
  /* ------------------------------------------------------------------ */
  function initStoryStepper() {
    var stories = document.querySelectorAll('[data-noir-story]');
    if (!stories.length) return;

    stories.forEach(function (story) {
      var steps = Array.prototype.slice.call(story.querySelectorAll('[data-noir-story-step]'));
      var media = story.querySelectorAll('[data-noir-story-media]');
      if (!steps.length) return;

      function setActive(index) {
        steps.forEach(function (step, i) {
          step.classList.toggle('is-active', i === index);
        });
        media.forEach(function (mediaEl) {
          mediaEl.querySelectorAll('[data-noir-story-frame]').forEach(function (frame, i) {
            frame.classList.toggle('is-active', i === index);
          });
        });
      }

      if (!('IntersectionObserver' in window)) {
        setActive(0);
        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              var index = steps.indexOf(entry.target);
              if (index !== -1) setActive(index);
            }
          });
        },
        { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
      );

      steps.forEach(function (step) {
        observer.observe(step);
      });

      setActive(0);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Personalization demo auto-cycle — cycles through example content on */
  /* a slow timer. Pauses on hover/focus and respects reduced motion.    */
  /* Usage: [data-noir-cycle] wrapping [data-noir-cycle-item] items.     */
  /* ------------------------------------------------------------------ */
  function initCyclers() {
    var cyclers = document.querySelectorAll('[data-noir-cycle]');
    if (!cyclers.length) return;

    cyclers.forEach(function (cycler) {
      var items = Array.prototype.slice.call(cycler.querySelectorAll('[data-noir-cycle-item]'));
      var dots = Array.prototype.slice.call(cycler.querySelectorAll('[data-noir-cycle-dot]'));
      if (items.length < 2) return;

      var current = 0;
      var timer = null;
      var interval = parseInt(cycler.getAttribute('data-noir-cycle-interval'), 10) || 3200;

      function show(index) {
        items.forEach(function (item, i) {
          item.classList.toggle('is-active', i === index);
        });
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === index);
          dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });
        current = index;
      }

      function next() {
        show((current + 1) % items.length);
      }

      function start() {
        if (reduceMotion || timer) return;
        timer = window.setInterval(next, interval);
      }

      function stop() {
        if (timer) {
          window.clearInterval(timer);
          timer = null;
        }
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          stop();
          show(i);
          start();
        });
      });

      cycler.addEventListener('mouseenter', stop);
      cycler.addEventListener('mouseleave', start);
      cycler.addEventListener('focusin', stop);
      cycler.addEventListener('focusout', start);

      show(0);
      start();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Muted proof videos — play only once scrolled into view, pause when  */
  /* out of view. Poster image covers the reduced-motion / no-JS case,   */
  /* so this is a pure progressive enhancement.                          */
  /* Usage: [data-noir-inview-video] on a <video muted loop playsinline>.*/
  /* ------------------------------------------------------------------ */
  function initInViewVideos() {
    if (reduceMotion) return;
    var videos = Array.prototype.slice.call(document.querySelectorAll('[data-noir-inview-video]'));
    if (!videos.length || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.play().catch(function () {});
          } else {
            entry.target.pause();
          }
        });
      },
      { threshold: 0.4 }
    );

    videos.forEach(function (video) {
      observer.observe(video);
    });
  }

  function init() {
    initParallax();
    initStoryStepper();
    initCyclers();
    initInViewVideos();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }
})();
